import { fetcher, useAuthFetch } from "matsuri-hooks"
import {
    Listing,
    ListingDetail,
    ListingFlag,
    ListingOpenStatus,
    ListingOperationValueType,
    ListingOwner
} from "../domain/listing"
import { m2mCore } from "../endpoints/m2m-core"
import { m2mCore_v1 } from "../endpoints/m2m-core.v1"

const orderOfListingOpenStatus = (s: ListingOpenStatus) => {
    switch (s) {
        case "PreOpen":
            return 1
        case "Opened":
            return 0
        case "Closed":
            return 2
        default:
            return 3
    }
}

export const compareListingOpenStatus = (
    s1: ListingOpenStatus,
    s2: ListingOpenStatus
) => {
    const orderOfS1 = orderOfListingOpenStatus(s1)
    const orderOfS2 = orderOfListingOpenStatus(s2)
    if (orderOfS1 < orderOfS2) {
        return -1
    } else if (orderOfS1 > orderOfS2) {
        return 1
    } else {
        return 0
    }
}

export const usePropertyRooms = (token: string, propertyId: string) => {
    return useAuthFetch<Listing[]>(
        token,
        m2mCore.v1.propertyListRooms({ id: propertyId }),
        {}
    )
}

export const useRoomDetail = (token: string, roomId: string | undefined) => {
    return useAuthFetch<ListingDetail>(
        token,
        roomId ? m2mCore.v1.listingDetail({ id: roomId }) : null,
        {}
    )
}

// type CreateListing struct {
// 	Address                            string                                `json:"address"`
// 	CityName                           string                                `json:"cityName"`
// 	MaxNumberOfGuests                  uint                                  `json:"maxNumberOfGuests"`
// 	Name                               string                                `json:"name"`
// 	PrefectureName                     string                                `json:"prefectureName"`
// 	Size                               float64                               `json:"size"`
// 	RegistrationNumber                 string                                `json:"registrationNumber"`
// 	RegistrationNumberIssueDate        *string                               `json:"registrationNumberIssueDate"`
// 	ListingMTLeaseOrAgencyContractSpan *ListingMTLeaseOrAgencyContractString `json:"listingMTLeaseOrAgencyContractSpan"`
// 	ListingAttractingCustomerSpan      *ListingAttractingCustomerString      `json:"listingAttractingCustomerSpan"`
// 	SettingUpStartStatus               *ListingSettingUpStartStatusString    `json:"settingUpStartStatus"`
// 	RoomTypeId                         string                                `json:"roomTypeId"`
// 	Memo                               string                                `json:"memo"`
// }
interface CreateListingInput {
    address: string
    cityName: string
    maxNumberOfGuests: number
    name: string
    prefectureName: string
    size: number
    registrationNumber: string
    registrationNumberIssueDate?: string
    listingMTLeaseOrAgencyContractSpan?: {
        mTLeaseOrAgencyContractStartDate: string
        mTLeaseOrAgencyContractStartFlag: ListingFlag
        mTLeaseOrAgencyContractEndDate: string
        mTLeaseOrAgencyContractEndFlag: ListingFlag
    }
    listingAttractingCustomerSpan?: {
        attractingCustomerEndDate: string
        attractingCustomerEndFlag: ListingFlag
    }
    settingUpStartStatus?: {
        settingUpStartDate: string
        settingUpStartFlag: ListingFlag
    }
    roomTypeId: string
    memo: string
}

interface CreateListingResponse {
    result: "ok"
    listingId: string
}

export const requestCreateListing = (
    token: string,
    input: CreateListingInput
) => {
    return fetcher<CreateListingResponse>(m2mCore.v1.createListing(), {
        method: m2mCore.v1.createListing.method,
        token,
        body: JSON.stringify(input)
    })
}

export const requestGetRoomDetail = (token: string, roomId: string) => {
    return fetcher<ListingDetail>(m2mCore.v1.listingDetail({ id: roomId }), {
        token
    })
}

export interface UpdateListingInput {
    address?: {
        prefectureId: string
        cityId: string
        rest: string
    }
    operationType?: ListingOperationValueType
    name?: string
    memo?: string
    registrationNumber?: string
    registrationNumberIssueDate?: string
    activeSpan?: {
        date: string
        activeStatus: "Open" | "Close"
    }
    mTLeaseOrAgencyContract?: {
        mTLeaseOrAgencyContractStartDate: string
        mTLeaseOrAgencyContractStartFlag: ListingFlag
        mTLeaseOrAgencyContractEndDate: string
        mTLeaseOrAgencyContractEndFlag: ListingFlag
    }
    attractingCustomer?: {
        monthlyAttractingCustomerStartDate?: string
        monthlyAttractingCustomerStartFlag?: ListingFlag
        minpakuAccommodationStartDate?: string
        minpakuAccommodationStartFlag?: ListingFlag
        attractingCustomerEndDate: string
        attractingCustomerEndFlag: ListingFlag
    }
    settingUpStartStatus?: {
        settingUpStartDate: string
        settingUpStartFlag: ListingFlag
    }
}

export const requestEditListing = (
    token: string,
    listingId: string,
    input: UpdateListingInput
) => {
    return fetcher(m2mCore.v1.updateListing({ id: listingId }), {
        method: "PUT",
        token,
        body: JSON.stringify(input)
    })
}

export const useListingOwner = (token: string, listingId: string) => {
    return useAuthFetch<ListingOwner>(
        token,
        m2mCore_v1.findOwnerByListingId({ listingId }),
        {}
    )
}

export const requestSearchListingsByName = (
    token: string,
    input: {
        name: string
        page: number
        itemsPerPage: number
    }
) => {
    return fetcher<{ listings?: Listing[] }>(m2mCore_v1.listingSearchByName(), {
        token,
        method: "POST",
        body: JSON.stringify(input)
    })
}

export const requestGetListingById = (token: string, listingId: string) => {
    return fetcher<ListingDetail>(m2mCore_v1.listingDetail({ id: listingId }), {
        token
    })
}

export const useSearchListings = (
    token: string,
    input?: {
        conditions: {
            ids?: string[]
        }
        pagination: {
            page: number
            itemsPerPage: number
        }
    }
) => {
    const endpoint = m2mCore_v1.searchListings

    return useAuthFetch<{ listings?: Listing[] }>(
        token,
        input ? endpoint() : null,
        {
            method: endpoint.method,
            body: JSON.stringify(input)
        }
    )
}
